import { useEffect, useState } from 'react';
import moment from 'moment';

import { Button, Input, Select, Form, Alert } from 'antd';

import notifier from 'utils/notifier';
import { ErrorResponse } from 'utils/types';
import { ProviderType, UserRole } from 'utils/enums';
import { selectDefaultProvider } from 'utils/helpers';

import { GetResponse, SearchFormProps } from 'store/authorization/faxes/actions';

import FloatLabel from 'components/Common/FloatLabel';
import DatePickerLocalised from 'components/Common/DatePickerLocalised';

import { ProviderList, FaxStatusList } from './Proptypes';

import './index.scss';

const { Option } = Select;

const SearchFaxForm = (props: SearchFormProps) => {
  const ALL_PROVIDERS = '{"Provider": "ALL PROVIDERS"}';
  const [birth, setBirth] = useState('');
  const [authStatus, setAuthStatus] = useState('');
  const [provider, setProvider] = useState(ALL_PROVIDERS);
  const [showHelpfulText, setShowHelpfulText] = useState(false);

  const [form] = Form.useForm();
  const [search, setSearch] = useState(false);

  //sets the provider dropdown to the login user as default provider
  useEffect(() => {
    if (props.providerList.length > 0) {
      setProvider(selectDefaultProvider(props.providerList, ALL_PROVIDERS, 'string'));
    }
  }, [props.providerList]);

  useEffect(() => {
    birth !== '' && form.validateFields();
  }, [birth]);

  useEffect(() => {
    search && onSearch();
  }, [props.onTableChange.current, props.onTableChange.pageSize])

  const parseAuthStatus = (authStatus: string) => {
    if(authStatus) {
      return JSON.parse(authStatus);
    } else {
      return null;
    }
  }

  const parseProvider = (provider: string) => {
    const providerObject = JSON.parse(provider);
    if (provider && providerObject.Provider === 'ALL PROVIDERS') {
      return { "Provider": providerObject.Provider, "RmiProvKeyId": null, "RmiCompanyId": null };
    }
    return providerObject;
  }

  const parseFaxStatus = (authStatus: string) => {
    if(authStatus) {
      const authStatusObject = JSON.parse(authStatus);
      return authStatusObject.RmiLetterTypeCode;
    } else {
      return null;
    }
  }

  const validationRules = async (fieldData: any, value: any) => {
    const charLessThanFive = (value: any) => fieldData.field === 'memberId' && value && value.trim().length < 5;

    const allEmptyFields = () => {
      return !form.getFieldValue('memberId') && !birth &&
        !form.getFieldValue('firstName') && !form.getFieldValue('lastName') &&
        !form.getFieldValue('authNo') &&
        !form.getFieldValue('authStatus');
    }

    const allFieldsResolved = () => {
      return (form.getFieldValue('memberId') && birth) ||
        (form.getFieldValue('firstName') && form.getFieldValue('lastName') && birth) ||
        form.getFieldValue('authNo') ||
        form.getFieldValue('authStatus');
    }

    if (charLessThanFive(value)) {
      return Promise.reject('Please enter at least 5 characters.');
    }

    if (allEmptyFields()) {
      setShowHelpfulText(true);
      return Promise.reject('Required');
    }

    if (allFieldsResolved()) {
      setShowHelpfulText(false);
      return Promise.resolve();
    }

    if (JSON.parse(provider || '{}').Provider === 'ALL PROVIDERS' || ![ProviderType.PCP, ProviderType.CLINIC].includes(JSON.parse(provider || '{}').RmiClass)) {
      const memberIdResolved = () => {
        return form.getFieldValue('memberId') || (form.getFieldValue('authStatus') || form.getFieldValue('authNo')) || (form.getFieldValue('firstName') && form.getFieldValue('lastName'));
      }

      if (fieldData.field === 'memberId') {
        if (memberIdResolved()) {
          setShowHelpfulText(false);
          return Promise.resolve();
        }

        if (birth) {
          setShowHelpfulText(false);
          return Promise.reject('Required');
        }
      }

      if (fieldData.field === 'birth') {
        const birthResolved = () => {
          return birth || (form.getFieldValue('authStatus') || form.getFieldValue('authNo'));
        }

        if (birthResolved()) {
          setShowHelpfulText(false);
          return Promise.resolve();
        }

        if (form.getFieldValue('memberId') || (form.getFieldValue('firstName') || form.getFieldValue('lastName'))) {
          setShowHelpfulText(false);
          return Promise.reject('Required');
        }
      }

      if (fieldData.field === 'authNo') {
        const authNoResolved = () => {
          return form.getFieldValue('authNo') || birth;
        }

        if (authNoResolved()) {
          setShowHelpfulText(false);
          return Promise.resolve();
        }
      }

      if (fieldData.field === 'authStatus') {
        const authStatusResolved = () => {
          return form.getFieldValue('authStatus') || birth;
        }

        if (authStatusResolved()) {
          setShowHelpfulText(false);
          return Promise.resolve();
        }
      }

      if (fieldData.field === 'firstName') {
        const firstNameResolved = () => {
          return (form.getFieldValue('firstName') || (form.getFieldValue('authStatus') || form.getFieldValue('authNo')) || form.getFieldValue('memberId')) || (birth && !form.getFieldValue('lastName'));
        }

        const missingDOB = () => {
          return form.getFieldValue('lastName') && !birth;
        }

        if (firstNameResolved()) {
          setShowHelpfulText(false);
          return Promise.resolve();
        }

        if (missingDOB()) {
          setShowHelpfulText(false);
          return Promise.reject('Required');
        }
      }

      if (fieldData.field === 'lastName') {
        const lastNameResolved = () => {
          return (form.getFieldValue('lastName') || (form.getFieldValue('authStatus') || form.getFieldValue('authNo')) || form.getFieldValue('memberId')) || (birth && !form.getFieldValue('firstName'));
        }

        const missingDOB = () => {
          return form.getFieldValue('firstName') && !birth;
        }

        if (lastNameResolved()) {
          setShowHelpfulText(false);
          return Promise.resolve();
        }

        if (missingDOB()) {
          setShowHelpfulText(false);
          return Promise.reject('Required');
        }
      }
    } else {

      // PCP (P) / Clinic (C) validation
      if (fieldData.field === 'memberId') {
        if (form.getFieldValue('memberId') || (form.getFieldValue('authStatus') || form.getFieldValue('authNo'))
          || (form.getFieldValue('firstName') && form.getFieldValue('lastName'))) {
          setShowHelpfulText(false);
          return Promise.resolve();
        }
      }

      if (fieldData.field === 'birth') {

        if (birth || (form.getFieldValue('authStatus') || form.getFieldValue('authNo')) || form.getFieldValue('memberId')) {
          setShowHelpfulText(false);
          return Promise.resolve();
        }

        if ((form.getFieldValue('firstName') || form.getFieldValue('lastName'))) {
          setShowHelpfulText(false);
          return Promise.reject('Required');
        }
      }

      if (fieldData.field === 'authNo') {
        if (form.getFieldValue('authNo') || birth) {
          setShowHelpfulText(false);
          return Promise.resolve();
        }
      }

      if (fieldData.field === 'authStatus') {
        if (form.getFieldValue('authStatus') || birth) {
          setShowHelpfulText(false);
          return Promise.resolve();
        }
      }

      if (fieldData.field === 'firstName') {
        if ((form.getFieldValue('firstName') || (form.getFieldValue('authStatus') || form.getFieldValue('authNo')) || form.getFieldValue('memberId')) || (birth && !form.getFieldValue('lastName'))) {
          setShowHelpfulText(false);
          return Promise.resolve();
        }

        if (form.getFieldValue('lastName') && !birth) {
          setShowHelpfulText(false);
          return Promise.reject('Required');
        }
      }

      if (fieldData.field === 'lastName') {
        if ((form.getFieldValue('lastName') || (form.getFieldValue('authStatus') || form.getFieldValue('authNo')) || form.getFieldValue('memberId')) || (birth && !form.getFieldValue('firstName'))) {
          setShowHelpfulText(false);
          return Promise.resolve();
        }

        if (form.getFieldValue('firstName') && !birth) {
          setShowHelpfulText(false);
          return Promise.reject('Required');
        }
      }
    }
  };

  const onSearch = () => {
    form.validateFields().then((values: any) => {
      let form_data = {
        "Status": parseAuthStatus(authStatus),
        "Provider": sessionStorage.getItem('user_role') !== UserRole.HEALTH_PLAN ? parseProvider(provider) : undefined,
        "AuthNo": values.authNo,
        "CompanyId": parseProvider(provider).RmiCompanyId,
        "DOB": birth === '' ? null : birth,
        "FaxStatus": parseFaxStatus(authStatus),
        "FaxDate": null,
        "FirstName": values.firstName,
        "index": 0,
        "LastName": values.lastName,
        "MembId": values.memberId,
        "PageIndex": props.onTableChange.current || 1,
        "PageSize": props.onTableChange.pageSize || 20,
        "ProvKeyId": parseProvider(provider).RmiProvKeyId,
        "UserId": sessionStorage.user_id,
        "ProviderName": sessionStorage.getItem('user_role') !== UserRole.HEALTH_PLAN ? parseProvider(provider).Provider : undefined,
        "UserRole": sessionStorage.user_role,
      };

      return props.getFax(form_data)
        .then((response: GetResponse[]) => {
          if (response.length === 0) {
            notifier.error(' There were no results for this search. Please check the fields and try again.');
            return;
          }
          setSearch(true);
          props.setCollapseState([]);
          //adding the key here to use it for the expand row later
          props.newTableDataHandler(response.map((obj: any, key: number ) => ({ ...obj, key: key })));
        })
        .catch((err: ErrorResponse) => {
          notifier.error(err.response?.data.Message || 'Something went wrong, please contact a system administrator.');
        });
    });
  };

  const onReset = () => {
    form.resetFields();
    setBirth('');
    setAuthStatus('');
    setProvider('{"Provider": "ALL PROVIDERS"}');
    setShowHelpfulText(false);
    return props.newTableDataHandler([]);
  };

  return (
    <div className="search-fax-page">
      <Form
        form={form}
        name="search-fax"
        onFinish={onSearch}
      >
        <div className="row">

          {sessionStorage.getItem('user_role') !== UserRole.HEALTH_PLAN &&
            <div className="col-lg-6">
              <FloatLabel label="Provider:" value={provider}>
                <Select
                  showSearch
                  value={provider}
                  placeholder="Select a Provider..."
                  onChange={value => setProvider(value)}
                >
                  <Option value='{"Provider": "ALL PROVIDERS"}'>ALL PROVIDERS</Option>
                  {props.providerList.map((option: ProviderList, key: number) => {
                      return <Option key={key} value={JSON.stringify(option)}>{option.Provider}</Option>;
                  })}
                </Select>
              </FloatLabel>
            </div>
          }

          <div className="col-lg-6">
            <FloatLabel
              name="authNo"
              label="Auth No"
              form={form}
              dependencies={['memberId', 'firstName', 'lastName', 'birth', 'authStatus']}
              validateTrigger={['onSubmit']}
              rules={[
                () => ({
                  validator: validationRules
                })
              ]}
            >
              <Input />
            </FloatLabel>
          </div>

          <div className="col-lg-6">
            <FloatLabel
              label="Status:"
              value={authStatus}
              name="authStatus"
              form={form}
              dependencies={['memberId', 'firstName', 'lastName', 'birth', 'authNo']}
              validateTrigger={['onSubmit']}
              rules={[
                () => ({
                  validator: validationRules
                })
              ]}
            >
              <Select
                value={authStatus}
                onChange={value => setAuthStatus(value)}
              >
              {props.faxStatusList.map((option: FaxStatusList, key: number) => {
                return <Option key={key} value={JSON.stringify(option)}>{option.RmiDescr.toUpperCase()}</Option>;
              })}
              </Select>
            </FloatLabel>
          </div>

          <div className="col-lg-6">
            <FloatLabel
              name="memberId"
              label="Member ID:"
              form={form}
              dependencies={['authNo', 'firstName', 'lastName', 'birth', 'authStatus']}
              validateTrigger={['onSubmit']}
              rules={[
                () => ({
                  validator: validationRules
                })
              ]}
            >
              <Input />
            </FloatLabel>
          </div>
        </div>

        <div className="row">
          <div className="col-lg-6">
            <FloatLabel
              name="lastName"
              label="Last Name"
              form={form}
              dependencies={['memberId', 'firstName', 'authNo', 'birth', 'authStatus']}
              validateTrigger={['onSubmit']}
              rules={[
                () => ({
                  validator: validationRules
                })
              ]}
            >
              <Input />
            </FloatLabel>
          </div>

          <div className="col-lg-6">
            <FloatLabel
              name="firstName"
              label="First Name"
              form={form}
              dependencies={['memberId', 'authNo', 'lastName', 'birth', 'authStatus']}
              validateTrigger={['onSubmit']}
              rules={[
                () => ({
                  validator: validationRules
                })
              ]}
            >
              <Input />
            </FloatLabel>
          </div>

          <div className="col-lg-6">
            <FloatLabel
              name="birth"
              label="DOB (MM/DD/YYYY)"
              value={birth}
              dependencies={['memberId', 'authNo', 'lastName', 'firstName', 'authStatus']}
              rules={[
                () => ({
                  validator: validationRules
                }),
                () => ({
                  async validator() {
                    const validDate = () => birth && moment(birth).isValid() && moment(birth).year() >= 1900 && birth.match(/^\d{2}\/\d{2}\/\d{4}$/);
                    if (validDate()) {
                      return Promise.resolve();
                    }

                    if (!showHelpfulText && birth) {
                      return Promise.reject(new Error('Invalid Date'));
                    }
                  }
                })
              ]}
            >
              <DatePickerLocalised
                date={birth}
                setDate={setBirth}
              />
            </FloatLabel>
          </div>

          <div className="col-lg-6 col-md-6 btn-wrapper">
            <Button className="search-fax-form-btn" type="primary" htmlType="submit" loading={props.isFetchingFaxes}>SEARCH</Button>
            <Button className="search-fax-form-reset-btn" onClick={onReset}>RESET</Button>
          </div>
        </div>
      </Form>
      {showHelpfulText ?
        <Alert type="error"
          message={
            (JSON.parse(provider || '{}').Provider === 'ALL PROVIDERS' || ![ProviderType.PCP, ProviderType.CLINIC].includes(JSON.parse(provider || '{}').RmiClass)) ?
            <>
              Please meet one of the following requirements:<br />
              MemberID + DOB (mm/dd/yyyy) <br />
              Auth No ONLY <br />
              Status ONLY <br />
              Last Name + First Name + DOB (mm/dd/yyyy)
            </>
              :
            <>
              Please meet one of the following requirements:<br />
              MemberID ONLY <br />
              Auth No ONLY <br />
              Status ONLY <br />
              Last Name + First Name ONLY <br />
              DOB (mm/dd/yyyy) ONLY
            </>
          }
        />
        : null}
    </div>
  );
}

export default SearchFaxForm;